// ===========================================================================
// CDCI — Clinical Documentation & Charge Intelligence
// Constantes del módulo (docs/modulos/BillingV2/02_CDCI_Descripcion_Modulo.md)
//
// NOTA: catálogos hardcodeados mientras el backend expone los maestros mdm.*
// (mdm.TAB_AMD_LOCATIONS, APPOINTMENT_TYPE, APPOINTMENT_STATUS — Paso 1 del
// roadmap). Cuando existan, estos se cargan vía GET api/amd/locations etc.
// ===========================================================================

export const CDCI_BASE_PATH = "/cdci";

// Nombre completo del módulo: la sigla sola no se entiende fuera del equipo,
// así que las páginas la muestran siempre expandida en el header.
export const CDCI_FULL_NAME = "Clinical Documentation & Charge Intelligence";

// Estados de cita (futuro maestro mdm APPOINTMENT_STATUS)
export const APPT_STATUS = {
  SCHEDULED:   { key: "SCHEDULED",   label: "Scheduled",    tone: "muted" },
  CONFIRMED:   { key: "CONFIRMED",   label: "Confirmed",    tone: "info" },
  CHECKED_IN:  { key: "CHECKED_IN",  label: "Checked in",   tone: "info" },
  IN_ROOM:     { key: "IN_ROOM",     label: "In room",      tone: "warn" },
  COMPLETED:   { key: "COMPLETED",   label: "Completed",    tone: "ok" },
  NO_SHOW:     { key: "NO_SHOW",     label: "No show",      tone: "crit" },
  CANCELLED:   { key: "CANCELLED",   label: "Cancelled",    tone: "muted" },
};

// Tipos de cita con color (futuro maestro getappttypes)
export const APPT_TYPES = {
  AWV:        { key: "AWV",        label: "Annual Wellness", color: "#1B5FA8" },
  FOLLOW_UP:  { key: "FOLLOW_UP",  label: "Follow Up",       color: "#0F6E6E" },
  TELEHEALTH: { key: "TELEHEALTH", label: "Telehealth",      color: "#7B5EA7" },
  HOSP_FU:    { key: "HOSP_FU",    label: "Hospital D/C FU", color: "#B5760A" },
  SICK:       { key: "SICK",       label: "Sick Visit",      color: "#C0392B" },
};

// Ciclo de vida de la nota del encuentro (entidad Note — Paso 4 del roadmap)
export const NOTE_STATUS = {
  NOT_STARTED: { key: "NOT_STARTED", label: "Not started", tone: "muted" },
  DRAFT:       { key: "DRAFT",       label: "Draft",       tone: "warn" },
  COMPLETE:    { key: "COMPLETE",    label: "Complete",    tone: "info" },
  // La clave interna sigue siendo SIGNED (todo el módulo la referencia), pero
  // el label visible es "Closed": en CDCI NO se firman notas — el cierre es
  // local y la firma oficial ocurre en AdvancedMD.
  SIGNED:      { key: "SIGNED",      label: "Closed",      tone: "ok" },
};

// Ciclo de vida de los cargos del encuentro (entidad Charge — Paso 5)
export const CHARGE_STATUS = {
  NOT_CAPTURED: { key: "NOT_CAPTURED", label: "Not captured", tone: "muted" },
  DRAFT:        { key: "DRAFT",        label: "Draft",        tone: "warn" },
  CAPTURED:     { key: "CAPTURED",     label: "Captured",     tone: "ok" },
};

// Pasos del journey del médico (§2 del documento maestro + auditoría 2026-10-21)
export const JOURNEY_STEPS = [
  { key: "schedule", label: "Schedule" },
  { key: "context",  label: "Patient Context" },
  { key: "note",     label: "Documentation" },
  // Auditoría regla-por-regla (api/audit-temp): la nota documentada se audita
  // y no se registra en AMD hasta pasar sin errores certificados.
  { key: "audit",    label: "Audit" },
  { key: "charges",  label: "Charge Capture" },
  // "Close", no "Sign & Close": en CDCI no se firma — la firma es en AMD.
  { key: "sign",     label: "Close" },
];

// Tabs del workspace del encuentro
export const ENCOUNTER_TABS = {
  CONTEXT: "context",
  NOTE: "note",
  MEAT: "meat",
  AUDIT: "audit",
  CHARGES: "charges",
  SIGN: "sign",
};

// Causas de visita — mismas opciones que el flujo AI Clinical Notes del
// Patient Map. Deciden el camino de la nota:
// - Sick / accidente / pre-op → nota normal (visita puntual).
// - Rutina / seguimiento / otro → el sistema ofrece los care gaps y coding
//   opportunities pendientes del paciente con asistencia IA.
export const REASON_OF_VISIT_OPTIONS = [
  "Sick",
  "Motor Vehicle Accident (MVA)",
  "Work Accident",
  "Pre-Op / Cosmetic - Surgical",
  "Annual or Lab Follow-Up",
  "Other",
];

// Razones que documentan la nota "normal", sin ofrecer gaps/coding por IA.
export const AI_SKIP_REASONS = new Set([
  "Sick",
  "Motor Vehicle Accident (MVA)",
  "Work Accident",
  "Pre-Op / Cosmetic - Surgical",
]);

/**
 * Charge Capture del encuentro — OCULTO desde 2026-10-21 (pedido del usuario):
 * los cargos aún NO se graban en AdvancedMD, así que el tab/paso se retira de
 * la vista hasta organizar ese circuito. Con el flag en false: el tab Charge
 * Capture y su paso del journey no se muestran, el checklist de cierre no
 * exige cargos capturados y el Close se habilita con la nota sola. Todo el
 * código (ChargesTab, quick-adds del puente nota→slip, stores) queda intacto —
 * poner true lo restaura completo.
 */
export const CHARGE_CAPTURE_ENABLED = false;

/**
 * Auditoría regla-por-regla del encuentro (tab Audit + gate de Insert/Close) —
 * OCULTA desde 2026-10-22 (pedido del usuario): su lugar en el flujo lo toma
 * la sección MEAT Criteria. Con el flag en false: el tab Audit y su paso del
 * journey no se muestran, y el Insert to AdvancedMD / checklist del Close
 * dejan de exigir la auditoría (auditGate viaja null). Todo el circuito
 * (AuditTab, useVisitAuditRun, noteAuditAdapter, snapshot de vigencia) queda
 * intacto — poner true lo restaura completo.
 */
export const AUDIT_ENABLED = false;

/**
 * Constructor de MEAT Criteria (tab MEAT Criteria del encuentro) — backend
 * api/meat-criteria/build (guía: docs/modulos/MeatCriteria/GUIA-FRONT-MEAT-CRITERIA.md).
 * Por cada diagnóstico documentado en la nota devuelve el ICD-10/HCC que la
 * documentación sustenta, qué componentes M.E.A.T. ya están documentados y un
 * MEAT propuesto (con placeholders) que el PCP revisa/edita y aprueba antes de
 * insertarlo a la nota. Backend apagado ⇒ el endpoint responde 503 y el tab lo
 * informa; este flag solo retira la sección de la vista si hiciera falta.
 */
export const MEAT_CRITERIA_ENABLED = true;

/**
 * Review de cobertura (care gaps & coding ops vs nota) + "Suggest fix"
 * post-review por item — backend CONFIRMADO 2026-10-22/23 (review-note y
 * suggest-coverage-fix implementados, tabla de errores definitiva en
 * docs/modulos/SugestVerification/CONFIRMACION-BACKEND-result6.md).
 *
 * ACTIVO desde 2026-10-23 para el smoke integrado (escenarios §8 de la spec).
 * Con el flag en true: panel de review en Documentación con botones Suggest
 * fix / Document now / Won't document por item, y GATE DURO en el Close
 * ("Care gaps & coding opportunities addressed" — cada item covered o
 * declined con razón), que convive con el gate de MEAT Criteria (MEAT inserta
 * evidencia → review valida cobertura). Si los endpoints aún no están
 * desplegados en el ambiente, el panel muestra "backend pending" y el gate
 * bloquea el cierre: apagar con false restaura el flujo sin review.
 */
export const COVERAGE_REVIEW_ENABLED = true;

/**
 * Panel "RAF Score (estimate)" (RafScorePanel, determinista con rafModel) —
 * OCULTO desde 2026-09-21 (decisión del usuario): el RAF del contexto lo
 * muestra la sección AI Reports (donuts de RAF Score / Future RAF Score).
 * El panel y rafModel quedan intactos; poner true lo devuelve al ContextTab.
 */
export const RAF_ESTIMATE_PANEL_ENABLED = false;
